/**
 * ASİSTAN STORE — sidebar orb'u, yüzen panel ve Geolop Asistanı efektlerinin ortak durumu.
 * - orb: canlı animasyon durumu (idle/thinking/speaking/listening)
 * - open: yazılı sohbet paneli açık mı
 * - voice: eller serbest SESLİ MOD (sidebar orb'una tıklayınca açılır/kapanır; panel gerekmez)
 * - highlight: asistan kullanıcıyı bir sayfaya götürdüğünde ekranda vurgulanacak kart kelimesi
 */
import { useSyncExternalStore } from "react";
import type { OrbState } from "@/components/assistant-orb";

export type TourStep = { path: string; label: string; text: string };
type AssistantState = { open: boolean; orb: OrbState; voice: boolean; highlight: string | null; tour: { steps: TourStep[]; i: number } | null; pendingPrompt: string | null };

let state: AssistantState = { open: false, orb: "idle", voice: false, highlight: null, tour: null, pendingPrompt: null };
const subs = new Set<() => void>();
function set(patch: Partial<AssistantState>) {
  state = { ...state, ...patch };
  subs.forEach((f) => f());
}

export const assistantStore = {
  toggle: () => set({ open: !state.open }),
  setOpen: (open: boolean) => { if (open !== state.open) set({ open }); },
  setOrb: (orb: OrbState) => { if (orb !== state.orb) set({ orb }); },
  toggleVoice: () => set({ voice: !state.voice }),
  setVoice: (voice: boolean) => { if (voice !== state.voice) set({ voice }); },
  setHighlight: (highlight: string | null) => set({ highlight }),
  /** Başka bir sayfadan asistana hazır istem yollar (ör. Planlama → "Strateji Seansı" düğmesi).
   *  Paneli açar; monte sohbet bileşeni istemi alıp kendisi gönderir. */
  ask: (text: string) => set({ open: true, pendingPrompt: text }),
  consumePendingPrompt: (): string | null => {
    const p = state.pendingPrompt;
    if (p) set({ pendingPrompt: null });
    return p;
  },
  // ═══ PLATFORM TURU — SESLE EŞLENİK: her durakta metin SESLİ okunur, ses bitmeden
  // sonraki durağa GEÇİLMEZ. Ses kapalıysa (ya da üretilemezse) okuma süresine göre
  // hesaplanmış bir bekleme uygulanır (kelime başına ~380ms, en az 5 sn) — acele yok.
  /** Monte olan sohbet bileşeni kendini konuşmacı olarak kaydeder; unmount'ta bırakır.
   *  Tur, her durakta O ANDA monte olan bileşenden konuşmasını ister — sayfa değişse de
   *  ses kesilmez ve KAPANMIŞ bileşen arkadan konuşmaya devam etmez. */
  setSpeaker: (fn: ((text: string) => Promise<void>) | null) => { speaker = fn; },
  hasSpeaker: () => !!speaker,
  startTour: (steps: TourStep[], speak?: (text: string) => Promise<void>) => {
    if (!steps.length) return;
    stopTourTimer();
    tourAborted = false;
    let i = 0;
    const runStep = async () => {
      if (tourAborted || i >= steps.length) { assistantStore.stopTour(); return; }
      const step = steps[i];
      set({ tour: { steps, i } });
      navigateTo(step.path);
      // Ekran çizilsin, sonra anlat (görsel ile ses aynı anda başlasın)
      await wait(600);
      if (tourAborted) return;
      // Konuşmacı: kayıtlı olan (güncel monte bileşen) önceliklidir; yoksa okuma süresi beklenir
      const say = speaker ?? speak;
      const basla = Date.now();
      // TAVAN SÜRE (canlı vaka 2026-08-12): 11. durakta TTS geldi ama ses hiç
      // çalmadı; 120 sn'lik oynatma bekçisi dolana kadar tur sessiz asılı kaldı,
      // kullanıcı "takıldı" diye kapattı. say() ne kadar takılırsa takılsın tur
      // okuma süresi + 15 sn içinde bir SONRAKİ durağa geçer.
      if (say) {
        try { await Promise.race([say(step.text), wait(readTime(step.text) + 15_000)]); }
        catch { /* ses üretilemedi — aşağıdaki taban süre devreye girer */ }
      }
      // TABAN SÜRE GARANTİSİ (tur bulgusu): konuşmacı sesi ÇALMADAN anında dönerse
      // (ses kapalı, TTS hatası, bileşen değişimi) durak yine okuma süresi kadar
      // ekranda kalır — kullanıcı kartı okuyamadan sayfa değişmez. 1.5 sn'den uzun
      // süren çağrı GERÇEK konuşma sayılır; ek bekleme uygulanmaz (ses zaten okudu).
      const gecen = Date.now() - basla;
      if (gecen < 1500) await wait(readTime(step.text) - gecen);
      if (tourAborted) return;
      await wait(1200);           // duraklar arası nefes payı
      i += 1;
      void runStep();
    };
    void runStep();
  },
  stopTour: () => {
    tourAborted = true;
    stopTourTimer();
    if (state.tour) set({ tour: null });
    navigateTo("/assistant");
  },
  /** Tur sırasında kullanıcı elle gezinirse turu bozmadan durdur */
  abortTourSilently: () => { tourAborted = true; stopTourTimer(); if (state.tour) set({ tour: null }); },
  get: () => state,
};

let tourAborted = false;
let speaker: ((text: string) => Promise<void>) | null = null;
function stopTourTimer() { /* zamanlayıcı yerine ardışık await zinciri kullanılıyor */ }
const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));
// Okuma süresi tahmini: kelime başına ~380 ms, 5-14 sn arasına sıkıştırılır
const readTime = (t: string) => Math.min(14000, Math.max(5000, t.trim().split(/\s+/).length * 380));
// wouter, popstate'i dinler — history push + popstate yayını her sayfadan gezinme sağlar
function navigateTo(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function useAssistantStore(): AssistantState {
  return useSyncExternalStore(
    (cb) => { subs.add(cb); return () => subs.delete(cb); },
    () => state,
  );
}
